import type { DsdEditorValueOccurrence } from "@/apis/dsd-editor/route";
import { cn } from "@/lib/utils";
import { NOTE_REFERENCE_SOFT_CLASS } from "@/utils/dsd-editor/note-reference-colors";
import {
  CircleAlert,
  CircleCheck,
  Info,
  NotepadText,
  TriangleAlert,
} from "lucide-react";
import { BetaFeatureMark, BetaFeatureOnly } from "@/components/beta-feature";
import { Switch } from "@/toggle-ui-kit/control";
import { Tooltip } from "@/toggle-ui-kit/tooltip";
import { memo } from "react";
import { DSD_REVIEW_COPY } from "../copy";
import { useDsdReviewEvidenceStore } from "../evidence/evidence-store";
import { DsdReviewGuideCard } from "../guide-card";
import { DsdReviewNoSelectionBox } from "./no-selection-box";
import { DsdReviewSelectedCellCard } from "./selected-cell-card";
import {
  SELECTED_EVIDENCE_CLASS,
  TONE_RING,
  tagToneOf,
} from "./review-card-parts";
import {
  DsdReviewJudgementStatus,
  DsdReviewMatchIndicator,
  DsdReviewTagChip,
} from "../review-indicators";
import {
  getDsdReviewEvidenceItems,
  isPendingReview,
  needsReview,
  type DsdReviewMatchItem,
  type DsdReviewPendingItem,
  type DsdReviewSelectedCellView,
} from "./selected-cell-view";

// 근거 창이 이 카드의 후보를 띄우고 있을 때 — 기본은 중립 선택 색(일치·검토 완료 카드),
// 검토 필요(노랑·빨강) 카드만 톤 테두리를 두고 톤 색으로 연하게 칠한다
const EVIDENCE_BG: Record<DsdReviewMatchItem["tone"], string> = {
  positive: "",
  caution: "bg-amber-50",
  negative: "bg-rose-50",
};

/**
 * 후보 카드. 세 상태 — 검토 불필요(흰 카드·초록 라벨), 검토 필요(톤 테두리·⚠·원형), 검토 완료(흐림·배지·메모·근거 확인만).
 * 검토 완료 카드의 사유(메모)를 누르면 저장된 판단으로 채운 같은 모달로 수정한다 — 배지는 표시만. 별도 해제(DELETE)는 없다
 */
const ReviewCard = ({
  item,
  showHardRule,
  onPreviewOccurrence,
  onShowEvidence,
  onReview,
  cellId,
  index,
}: {
  item: DsdReviewMatchItem;
  showHardRule: boolean;
  onPreviewOccurrence: (occurrence: DsdEditorValueOccurrence | null) => void;
  onShowEvidence: (occurrence: DsdEditorValueOccurrence) => void;
  onReview: (item: DsdReviewPendingItem) => void;
  /** 선택 셀과 이 후보의 순번 — 근거 창이 이 후보를 띄우고 있는지 카드가 직접 본다 */
  cellId: string;
  index: number;
}) => {
  const panelCopy = DSD_REVIEW_COPY.panel;
  // 검토 전(미판단) 카드만 원형을 달고 모달을 연다 — 타입 가드로 좁혀 target 없는 카드가 모달로 못 가게
  const pendingItem = isPendingReview(item) ? item : null;
  // 판단 저장된 카드 — 배지를 누르면 같은 모달을 저장된 판단으로 채워 연다 (수정). 별도 해제는 없다
  const savedReview = item.review;
  const reviewedItem = needsReview(item) && savedReview ? item : null;
  // 청크 쌍(보여줄 셀 없음)은 미리보기할 셀이 없다 — 합성 occurrence 의 청크 id 가 셀로 잡히지 않게
  const previewOccurrence = item.hasEvidence ? item.occurrence : null;
  // 카드 단위 구독 — ←/→ 로 후보가 바뀌면 이전·다음 카드 둘만 다시 렌더된다 (패널은 그대로)
  const showingEvidence = useDsdReviewEvidenceStore(
    (state) => state.target?.cellId === cellId && state.target.index === index,
  );
  const pending = pendingItem !== null;
  const reviewed = item.review !== null;
  const tag = panelCopy.tag[item.tag];
  return (
    <div
      role="group"
      aria-label={item.title}
      onMouseEnter={() => onPreviewOccurrence(previewOccurrence)}
      onMouseLeave={() => onPreviewOccurrence(null)}
      onFocus={() => onPreviewOccurrence(previewOccurrence)}
      onBlur={() => onPreviewOccurrence(null)}
      // 카드 어디를 눌러도 근거 확인 — 안의 버튼은 자기 동작만 (전파를 막는다).
      // 값·제목을 드래그해 복사한 뒤의 click 은 무시한다 (근거 창이 바뀌고 본문이 스크롤되면 안 된다)
      onClick={() => {
        if (window.getSelection()?.toString()) return;
        onShowEvidence(item.occurrence);
      }}
      className={cn(
        "flex cursor-pointer flex-col gap-2 rounded-[12px] bg-background-base p-4 ring-1 ring-inset",
        pending
          ? [TONE_RING[item.tone], showingEvidence && EVIDENCE_BG[item.tone]]
          : showingEvidence
            ? SELECTED_EVIDENCE_CLASS
            : "ring-line-normal",
      )}
    >
      <div className="flex items-center gap-2">
        <DsdReviewTagChip
          label={tag}
          tone={tagToneOf(item)}
          className={cn(reviewed && "opacity-50")}
        />
        <p
          className={cn(
            "label-1-normal-semibold min-w-0 flex-1 truncate text-label-normal",
            reviewed && "opacity-50",
          )}
        >
          {item.title}
          {pending && (
            <TriangleAlert
              aria-hidden
              className={cn(
                "ml-1 inline size-4 align-text-bottom",
                item.tone === "negative" ? "text-rose-500" : "text-orange-500",
              )}
            />
          )}
        </p>
        {pendingItem && (
          // 검토 전: 회색 테두리의 빈 원 (RoundCheckbox 의 unchecked 는 채워진 회색 원이라 안 맞음)
          <BetaFeatureOnly>
            <Tooltip label={panelCopy.reviewTooltip} side="top" align="end">
              <button
                type="button"
                aria-label={panelCopy.review}
                onClick={(event) => {
                  event.stopPropagation();
                  onReview(pendingItem);
                }}
                className="size-5 shrink-0 rounded-full border-2 border-line-normal transition-colors hover:border-label-alternative"
              />
            </Tooltip>
          </BetaFeatureOnly>
        )}
        {reviewedItem && savedReview && (
          // 판단 표시만 — 수정은 아래 사유 상자를 눌러서
          <DsdReviewJudgementStatus judgement={savedReview.judgement} />
        )}
      </div>
      {item.axisLabel && (
        <p
          className={cn(
            "label-2-regular text-label-alternative",
            reviewed && "opacity-50",
          )}
        >
          {item.axisLabel}
        </p>
      )}
      {showHardRule && item.hardRule && (
        // 상단 "하드룰 연결" 토글(베타)을 켠 동안만 — 문서의 빗금 셀과 같은 기준(link.hardRule).
        // 토글이 베타 여부를 이미 포함하므로 가리진 않고 인스펙터 표식만 단다
        <BetaFeatureMark>
          <p className="label-2-regular text-primary-color">
            {panelCopy.hardRule}: {panelCopy.hardRuleLabel(item.hardRule)}
          </p>
        </BetaFeatureMark>
      )}
      <div className="flex items-center justify-between gap-2">
        <p
          className={cn(
            "headline-2-semibold text-label-normal",
            reviewed && "opacity-50",
          )}
        >
          {item.value || panelCopy.emptyValue}
        </p>
        {/* 검토 완료 카드는 일치/불일치 라벨은 접고 근거 확인 버튼만 남긴다 (판단 뒤에도 근거를 다시 볼 수 있게) */}
        <div className="flex items-center gap-2">
          {!reviewed && (
            <DsdReviewMatchIndicator
              label={item.matchLabel}
              positive={item.matchTone === "positive"}
            />
          )}
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onShowEvidence(item.occurrence);
            }}
            className="rounded-small bg-background-gray-alternative px-2.5 py-1 label-2-medium text-label-normal transition-colors hover:bg-decorate-neutral/12"
          >
            {panelCopy.evidence}
          </button>
        </div>
      </div>
      {reviewedItem && savedReview?.reason && (
        // 사유를 누르면 저장된 판단·사유로 채운 같은 모달 (수정). 접근성 이름 = 동작(숨김 글자) + 사유 — 사유를 덮지 않는다.
        // 수정 진입은 베타 — 끄면 같은 모양의 글자만 (저장된 판단 배지처럼 표시는 남긴다)
        <BetaFeatureOnly
          fallback={
            <p className="flex items-center gap-2 rounded-[8px] bg-background-neutral-alternative px-2 py-1.5 label-2-regular text-label-normal">
              <NotepadText
                aria-hidden
                className="size-4 shrink-0 text-label-alternative"
              />
              {savedReview.reason}
            </p>
          }
        >
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onReview(reviewedItem);
            }}
            className="flex items-center gap-2 rounded-[8px] bg-background-neutral-alternative px-2 py-1.5 text-left label-2-regular text-label-normal transition-colors hover:bg-decorate-neutral/12"
          >
            <NotepadText
              aria-hidden
              className="size-4 shrink-0 text-label-alternative"
            />
            <span className="sr-only">
              {DSD_REVIEW_COPY.evidence.editReview}:
            </span>{" "}
            {savedReview.reason}
          </button>
        </BetaFeatureOnly>
      )}
      {/* 임시 표시 — 사유가 없어도 보여야 해서 사유 상자 밖에 둔다 */}
      {reviewedItem && savedReview?.inheritedFromFilename && (
        <p className="label-2-regular text-label-alternative">
          {panelCopy.inherited}
        </p>
      )}
    </div>
  );
};

const StatusBanner = ({
  banner,
}: {
  banner: DsdReviewSelectedCellView["banner"];
}) => {
  if (!banner) return null;
  const { tone, text } = banner;
  const Icon = tone === "positive" ? CircleCheck : CircleAlert;
  return (
    <div
      className={cn(
        "flex items-center gap-2 rounded-[8px] px-4 py-3 label-1-normal-medium",
        NOTE_REFERENCE_SOFT_CLASS[tone],
      )}
    >
      <Icon className="size-4 shrink-0" />
      <span>{text}</span>
    </div>
  );
};

/**
 * 선택 셀 요약 + 상태 + 값 후보 카드.
 * memo: 문서 조립 컴포넌트는 카드 호버(미리보기 셀)·스크롤 요청마다 다시 렌더되는데 패널은 그 값을 안 쓴다.
 * 호출측이 콜백 참조를 고정해야 효과가 있다
 */
export const DsdReviewMatchingPanel = memo(function DsdReviewMatchingPanel({
  showHardRule,
  count,
  hasChecks,
  selected,
  onScrollToCell,
  onPreviewOccurrence,
  onShowEvidence,
  onReview,
  pendingOnly,
  onPendingOnlyChange,
}: {
  /** 상단 "하드룰 연결" 토글(베타) — 켠 동안 카드에 규칙 id 를 보인다 */
  showHardRule: boolean;
  count: number;
  /** 매칭 결과(값 매칭 또는 주석 참조 체크)가 하나라도 있는지. 배지(count)와 같은 소스에서 판정한다 */
  hasChecks: boolean;
  selected: DsdReviewSelectedCellView | null;
  onScrollToCell: () => void;
  onPreviewOccurrence: (occurrence: DsdEditorValueOccurrence | null) => void;
  /** "근거 확인" — 그 후보의 주석을 선택 셀 아래 근거 창으로 */
  onShowEvidence: (occurrence: DsdEditorValueOccurrence) => void;
  /** 검토 필요 카드의 원형 — 검토 모달 */
  onReview: (item: DsdReviewPendingItem) => void;
  /** "검토 필요 항목만 보기" — 세션 안에서만 기억 */
  pendingOnly: boolean;
  onPendingOnlyChange: (pendingOnly: boolean) => void;
}) {
  const panelCopy = DSD_REVIEW_COPY.panel;
  // 보이는 후보 = 근거 창이 도는 후보. index 가 근거 창의 1/N 과 같은 번호
  const visibleItems = getDsdReviewEvidenceItems(
    selected?.items ?? [],
    pendingOnly,
  ).map((item, index) => ({ item, index }));
  const groups = (["required", "additional"] as const)
    .map((group) => ({
      group,
      items: visibleItems.filter(({ item }) => item.group === group),
    }))
    .filter(({ items }) => items.length > 0);
  const hasReviewTargets = (selected?.items ?? []).some(needsReview);
  // 필터를 켰는데 검토 쌍이 전부 판단됐다 — 빈 패널 대신 "모든 항목을 검토했어요" (Figma 7969-522004).
  // 검토 쌍이 처음부터 없는 셀은 토글이 없고 필터도 걸리지 않는다(getDsdReviewEvidenceItems)
  const allReviewed =
    pendingOnly && hasReviewTargets && visibleItems.length === 0;
  // 이 본문 셀에 걸린 서로 다른 하드룰 — 같은 규칙이 여러 셀을 이어도 하나로 센다. 몇 개가 가능한지(분모)는 프론트가 모른다
  const hardRules = [
    ...new Set(
      (selected?.items ?? []).flatMap((item) =>
        item.hardRule ? [item.hardRule] : [],
      ),
    ),
  ];

  return (
    <div className="flex h-full flex-col gap-4 overflow-y-auto overscroll-contain p-4">
      <DsdReviewGuideCard
        mode="note-reference-review"
        count={count}
        variant="embedded"
        collapsed={!!selected}
      />
      {selected ? (
        <>
          <DsdReviewSelectedCellCard
            sectionName={selected.sectionName}
            axisLabel={selected.axisLabel}
            value={selected.value}
            onScrollToCell={onScrollToCell}
          />
          <StatusBanner banner={selected.banner} />
          {showHardRule && hardRules.length > 0 && (
            // 상단 "하드룰 연결" 토글(베타)을 켠 동안만 — 카드마다 적힌 규칙 id 를 셀 단위로 모아 센다
            <BetaFeatureMark>
              <p className="flex flex-wrap items-center gap-1.5 rounded-[8px] bg-background-neutral-alternative px-3 py-2 label-2-regular text-label-normal">
                <span className="label-2-medium">
                  {panelCopy.hardRuleCount(hardRules.length)}
                </span>
                {hardRules.map((rule) => (
                  <span
                    key={rule}
                    className="rounded-small bg-background-base px-1.5 py-0.5 text-primary-color ring-1 ring-inset ring-line-normal"
                  >
                    {panelCopy.hardRuleLabel(rule)}
                  </span>
                ))}
              </p>
            </BetaFeatureMark>
          )}
          {selected.noLinkedNote && selected.items.length === 0 && (
            // 모든 항목을 검토했어요 와 같은 모양 (테두리 없는 가운데 문구)
            <p className="flex h-[200px] items-center justify-center label-2-regular text-neutral-700">
              {panelCopy.noLinkedNote}
            </p>
          )}
          {/* 검토 완료 처리 관련 UI 는 베타 — 끄면 토글 줄이 통째로 빠져 아래가 그대로 올라온다 */}
          {hasReviewTargets && (
            <BetaFeatureOnly>
              <label className="flex items-center justify-end gap-2">
                <span className="label-2-medium text-neutral-500">
                  {panelCopy.pendingOnly}
                </span>
                <Switch
                  size={24}
                  color="blue"
                  active={pendingOnly}
                  onChange={onPendingOnlyChange}
                  aria-label={panelCopy.pendingOnly}
                />
              </label>
            </BetaFeatureOnly>
          )}
          {allReviewed && (
            <section className="flex flex-col gap-2">
              <h4 className="label-1-normal-semibold text-label-normal">
                {panelCopy.requiredItems}
              </h4>
              <p className="flex h-[200px] items-center justify-center label-2-regular text-neutral-700">
                {panelCopy.allReviewed}
              </p>
            </section>
          )}
          {groups.map(({ group, items }, groupIndex) => (
            <section key={group} className="flex flex-col gap-2">
              <h4 className="label-1-normal-semibold text-label-normal">
                {group === "required"
                  ? panelCopy.requiredItems
                  : panelCopy.additionalItems}
              </h4>
              {/* 안내 상자는 첫 섹션 아래에만, "검토 필요 항목만 보기" 와 무관하게. 전부 검토해 이 목록이 비면("모든 항목을 검토했어요") 체크할 것이 없어 함께 빠진다 */}
              {groupIndex === 0 && hasReviewTargets && (
                <BetaFeatureOnly>
                  <p className="flex items-start gap-2 whitespace-pre-line rounded-[8px] bg-background-neutral-alternative p-2 label-2-regular text-label-normal">
                    <Info className="mt-0.5 size-4 shrink-0 fill-label-normal text-static-white" />
                    {panelCopy.reviewHint}
                  </p>
                </BetaFeatureOnly>
              )}
              {items.map(({ item, index }) => (
                <ReviewCard
                  key={item.key}
                  item={item}
                  showHardRule={showHardRule}
                  onPreviewOccurrence={onPreviewOccurrence}
                  onShowEvidence={onShowEvidence}
                  onReview={onReview}
                  cellId={selected.cellId}
                  index={index}
                />
              ))}
            </section>
          ))}
        </>
      ) : (
        <DsdReviewNoSelectionBox
          message={hasChecks ? undefined : DSD_REVIEW_COPY.matching.noChecks}
        />
      )}
    </div>
  );
});
